import React, {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  useId,
} from "react";

import { tw } from "~/shared";
import { ErrorText } from "~/ui/common/ErrorText";

interface InputProps
  extends ComponentPropsWithoutRef<"input">, PropsWithChildren {
  label?: string;
  error?: string;
  defaultValue?: string;
  isError?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  description?: string;
  containerClassName?: string;
  right?: React.ReactNode;
  inputType?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      error,
      type = "text",
      inputType,
      placeholder,
      className,
      defaultValue,
      children,
      isError = false,
      inputClassName,
      labelClassName,
      description,
      containerClassName,
      right,
      required = false,
      "aria-describedby": ariaDescribedBy,
      ...props
    }: InputProps,
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    return (
      <div
        className={tw(
          "flex w-full flex-col gap-0 font-light text-gray-700",
          containerClassName,
        )}
      >
        {(label ?? description) && (
          <div className="flex w-full flex-col">
            {label && (
              <label
                className={tw(
                  "text-indigo flex w-full text-base",
                  labelClassName,
                )}
                htmlFor={inputId}
              >
                <span className="first-letter:capitalize">
                  {label}
                  {required && <span className="pl-0.5"> *</span>}
                </span>
              </label>
            )}
            {description && <p className="text-sm">{description}</p>}
          </div>
        )}
        <div
          className={tw(
            "bg-neutral-25 border-blueGrey focus-within:border-violet focus-within:ring-violet flex w-full items-center justify-between rounded-md border text-sm focus-within:ring-1",
            className,
            (isError || error) && "bg-error border-red-600",
          )}
        >
          <input
            className={tw(
              "placeholder:text-input-placeholder h-11 w-full rounded border-0 bg-transparent px-4 py-3 text-base text-gray-800 placeholder:text-left focus:outline-none focus:ring-0 disabled:bg-gray-200 disabled:text-gray-700",
              inputClassName,
              "focus:placeholder-transparent",
            )}
            id={inputId}
            placeholder={placeholder ?? `Enter ${label?.toLocaleLowerCase()}`}
            {...props}
            type={inputType ?? type}
            required={required}
            aria-required={required}
            defaultValue={defaultValue}
            aria-describedby={error?.trim() ? errorId : ariaDescribedBy}
            aria-invalid={isError || !!error}
            ref={ref}
          />
          {children}

          {right && <span className="flex items-center p-2">{right}</span>}
        </div>
        {error?.trim() && <ErrorText id={errorId}>{error}</ErrorText>}
      </div>
    );
  },
);
